import {
  DEVICE_TYPES as T,
  GATE_TYPES,
  MODELS as M,
  ON_OFF_ACTION_MODELS,
  TRAITS,
  hasTrait,
  isModel,
  isType,
  typeOf,
} from "../constants/devices";

// What an automation sends to each device, as the app's app/automation/actions.js:
// the same command a tap on the device sends (SwitchComponent.js updateState and
// the device screen in CommonSwichDetails.js), for switching on and for
// switching off. `state` says which; the command carries the device's value.

const SWITCH_LIKE = [T.SWITCH, T.LIGHT, T.SOCKET];

const pinOf = (device) => Number(device?.pin) || 1;
// commands carry the trait as a number, as the app sends it
const command = (device, trait, value, id = pinOf(device)) => ({ id, trait: Number(trait), value });

const ON_OFF = [
  { key: "on", label: "ON" },
  { key: "off", label: "OFF" },
];
const OPEN_CLOSE = [
  { key: "on", label: "OPEN" },
  { key: "off", label: "CLOSE" },
];
const START_STOP = [
  { key: "on", label: "START" },
  { key: "off", label: "STOP" },
];
const ALARM_MODES = [
  { key: "away", label: "ARM AWAY" },
  { key: "stay", label: "STAY ARM" },
  { key: "off", label: "DISARM" },
];

// The water level controller's motors, by how its water flow is set up
function wlcMotors(device) {
  if (device.waterFlowType === "TYPE_4") return [1];
  if (device.waterFlowType === "TYPE_5") return [2];
  return [1, 2];
}

// How each kind of device can be set, in the app's order; the first that fits
// wins. `when` picks the devices and `kind` gives their choices (ON/OFF,
// OPEN/CLOSE...), an optional level (fan speed, brightness, motor), and the
// action for a choice.
const KINDS = [
  // W*SM switches, lights and sockets: trait 101 on their pin
  {
    when: (d) => isType(d, ...SWITCH_LIKE) && isModel(d, ...ON_OFF_ACTION_MODELS) && hasTrait(d, TRAITS.ON_OFF),
    kind: (d) => ({
      choices: ON_OFF,
      build: (choice) => ({ state: choice, command: command(d, TRAITS.ON_OFF, choice === "on" ? 1 : 0) }),
    }),
  },
  { when: (d) => isType(d, ...SWITCH_LIKE), kind: () => ({ choices: ON_OFF, build: (choice) => ({ state: choice }) }) },
  {
    when: (d) => isType(d, T.FAN) && isModel(d, M.W1F6SM_1) && hasTrait(d, TRAITS.FAN_SPEED),
    kind: (d) => ({
      choices: ON_OFF,
      level: { label: "Speed", values: [1, 2, 3, 4, 5, 6], initial: 3, on: "on" },
      build: (choice, speed) => ({ state: choice, command: command(d, TRAITS.FAN_SPEED, choice === "on" ? speed || 3 : 0) }),
    }),
  },
  {
    when: (d) => isType(d, T.FAN) && isModel(d, M.W1F6SM_1) && hasTrait(d, TRAITS.ON_OFF),
    kind: (d) => ({
      choices: ON_OFF,
      build: (choice) => ({ state: choice, command: command(d, TRAITS.ON_OFF, choice === "on" ? 1 : 0) }),
    }),
  },
  // older fans take a speed with "on"
  {
    when: (d) => isType(d, T.FAN),
    kind: () => ({
      choices: ON_OFF,
      level: { label: "Speed", values: [1, 2, 3, 4, 5], initial: 3, on: "on" },
      build: (choice, speed) => (choice === "on" ? { state: "on", speed: speed || 3 } : { state: "off" }),
    }),
  },
  { when: (d) => isType(d, T.MOTOR), kind: () => ({ choices: ON_OFF, build: (choice) => ({ state: choice }) }) },
  {
    when: (d) => isType(d, T.WATER_LEVEL_CONTROLLER) && hasTrait(d, TRAITS.MOTOR),
    kind: (d) => {
      const motors = wlcMotors(d);
      return {
        choices: ON_OFF,
        level: motors.length > 1 ? { label: "Motor", values: motors, initial: motors[0], always: true } : null,
        build: (choice, motor) => ({
          state: choice,
          command: command(d, TRAITS.MOTOR, choice === "on" ? 1 : 0, motor || motors[0]),
        }),
      };
    },
  },
  {
    when: (d) => isType(d, T.DIMMER_MODULE) && hasTrait(d, TRAITS.DIMMER),
    kind: (d) => ({
      choices: ON_OFF,
      level: { label: "Brightness", values: [10, 25, 50, 75, 100], initial: 100, unit: "%", on: "on" },
      build: (choice, brightness) => ({ state: choice, command: command(d, TRAITS.DIMMER, choice === "on" ? brightness || 100 : 0) }),
    }),
  },
  // as scenes send it: W1CM_1 opens with value 0 and closes with 1; W2CM_1's
  // first motor opens on id 1, closes on id 2
  {
    when: (d) => isType(d, T.CURTAIN_MODULE) && isModel(d, M.W1CM_1),
    kind: (d) => ({
      choices: OPEN_CLOSE,
      build: (choice) => ({ state: choice, command: command(d, TRAITS.CURTAIN, choice === "on" ? 0 : 1, 1) }),
    }),
  },
  {
    when: (d) => isType(d, T.CURTAIN_MODULE) && isModel(d, M.W2CM_1),
    kind: (d) => ({
      choices: OPEN_CLOSE,
      build: (choice) => ({ state: choice, command: command(d, TRAITS.CURTAIN, 1, choice === "on" ? 1 : 2) }),
    }),
  },
  // WGC gates: a swing gate's value says open (1) or close (2), the others open
  // on id 1 and close on id 2
  {
    when: (d) => isType(d, ...GATE_TYPES) && isModel(d, M.WGC_1, M.WGC_2),
    kind: (d) => {
      const swing = isType(d, T.SWING_GATE);
      return {
        choices: OPEN_CLOSE,
        build: (choice) => ({
          state: choice,
          command: swing
            ? command(d, TRAITS.GATE, choice === "on" ? 1 : 2, 1)
            : command(d, TRAITS.GATE, 1, choice === "on" ? 1 : 2),
        }),
      };
    },
  },
  // the first gate controller (GC_1): action 0 opens, 1 closes; rl is the relay
  {
    when: (d) => isType(d, ...GATE_TYPES),
    kind: (d) => {
      const swing = isType(d, T.SWING_GATE);
      return {
        choices: OPEN_CLOSE,
        build: (choice) => ({
          state: choice,
          gc: { action: choice === "on" ? 0 : 1, rl: swing ? Number(d.pin) : choice === "on" ? 1 : 2 },
        }),
      };
    },
  },
  {
    when: (d) => isType(d, T.ENERGY_METER) && hasTrait(d, TRAITS.METER_RELAY),
    kind: (d) => ({
      choices: ON_OFF,
      build: (choice) => ({ state: choice, command: command(d, TRAITS.METER_RELAY, choice === "on" ? 1 : 0, 1) }),
    }),
  },
  {
    when: (d) => isType(d, T.ROBOT_VACUUM_CLEANER) && isModel(d, M.WGRVC_1, M.WGRVC_2),
    kind: (d) => ({
      choices: START_STOP,
      build: (choice) => ({ state: choice, command: command(d, TRAITS.VACUUM_POWER, choice === "on" ? 1 : 0, 1) }),
    }),
  },
  {
    when: (d) => isType(d, T.SECURITY_ALARM) && isModel(d, M.WZLTEG_1),
    kind: (d) => ({
      choices: ALARM_MODES,
      build: (choice) => ({
        state: choice === "off" ? "off" : "on",
        command: command(d, TRAITS.SECURITY_MODE, { away: 0, stay: 1, off: 2 }[choice], 1),
      }),
    }),
  },
];

// How a device can be set in an automation: { choices, level?, build(choice,
// level) }. null when it can't be set from here yet (RGB lights and IR remotes
// need their own pickers; sensors start automations instead).
export function actionKind(device) {
  if (!device) return null;
  const entry = KINDS.find((item) => item.when(device));
  return entry ? entry.kind(device) : null;
}

// Back from a saved action to the picker's choice and level, for editing
export function choiceOf(action, device) {
  const type = typeOf(device);
  const cmd = action.command || {};
  if (type === T.SECURITY_ALARM) return { choice: ["away", "stay", "off"][cmd.value] || "off" };
  if (type === T.WATER_LEVEL_CONTROLLER) return { choice: action.state, level: cmd.id };
  if (type === T.FAN) return { choice: action.state, level: cmd.trait === Number(TRAITS.FAN_SPEED) ? cmd.value || undefined : action.speed };
  if (type === T.DIMMER_MODULE) return { choice: action.state, level: cmd.value || undefined };
  return { choice: action.state };
}

// How an action reads on cards and pages: "on", "off", "open", "on, speed 3",
// "motor 2 on".
export function actionLabel(action, device) {
  if (action.state === "toggle") return "toggle";
  const type = typeOf(device);
  const cmd = action.command || {};
  const on = action.state === "on";
  if (GATE_TYPES.includes(type) || type === T.CURTAIN_MODULE) return on ? "open" : "close";
  if (type === T.ROBOT_VACUUM_CLEANER) return on ? "start" : "stop";
  if (type === T.SECURITY_ALARM) return ["arm away", "stay arm", "disarm"][cmd.value] || (on ? "arm" : "disarm");
  if (type === T.WATER_LEVEL_CONTROLLER && cmd.id) return `motor ${cmd.id} ${on ? "on" : "off"}`;
  if (type === T.FAN && on) {
    const speed = cmd.trait === Number(TRAITS.FAN_SPEED) ? cmd.value : action.speed;
    return speed ? `on, speed ${speed}` : "on";
  }
  if (type === T.DIMMER_MODULE && on && cmd.value) return `on, ${cmd.value}%`;
  return on ? "on" : "off";
}
